import type { RequestOptions } from '@/interceptors/request';

// 定义 401 处理回调类型
type UnauthorizedHandler = () => { resetStore: () => void; automaticLogin: () => Promise<any> } | null;

// 存储 401 处理回调
let unauthorizedHandler: UnauthorizedHandler | null = null;

// 注册 401 处理回调
export const setUnauthorizedHandler = (handler: UnauthorizedHandler) => {
  unauthorizedHandler = handler;
};

export enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
}

export const http = <T>(options: RequestOptions) => {
  return new Promise<ResponseData<T> | T | UniApp.RequestSuccessCallbackResult>((resolve, reject) => {
    uni.request({
      ...options,
      dataType: 'json',
      // 响应成功
      success: async (res: Recordable) => {
        // 状态码 2xx，参考 axios 的设计
        if (res.statusCode < 200 || res.statusCode >= 300) {
          // 其他错误
          const error = (res.data as ResponseData<T>)?.message || '请求错误';
          uni.showModal({
            title: '提示',
            showCancel: false,
            content: String(error),
            complete: () => reject(error),
          });
          return reject(error);
        }

        // 2.1 提取核心数据 res.data
        const { code, data, message: msg } = res.data as ResponseData<T>;
        if (code === 200) {
          if (options.returnNativeResponse) {
            return resolve(res as any);
          }

          if (options.isTransformResponse === false) {
            return reject(res.data);
          }

          return resolve(data);
        }

        if (options.toast !== false && msg !== '登录信息已过期') {
          // 处理一下这个特殊的返回msg，Token过期后将进行无感刷新
          uni.$u.toast(msg || '请求错误');
        }

        // 这里需要触发重新登录
        if (code === 401) {
          const userStore = unauthorizedHandler ? unauthorizedHandler() : null;
          if (userStore) {
            userStore.resetStore();
          }

          // @ts-ignore
          const awaitLoginToRetry = async () => {
            await uni.$u.sleep();
            if (uni.isLoggedIn === true) {
              return awaitLoginToRetry();
            }

            if (uni.isInited === 'error') {
              throw new Error('自动登录失败，请重新登录');
            }

            const response = (await http(options)) as ResponseData<T>;
            return resolve(response);
          };

          try {
            if (uni.isLoggedIn === true) {
              return await awaitLoginToRetry();
            }

            if (userStore) {
              await userStore.automaticLogin();
            }
            const response = (await http(options)) as ResponseData<T>;
            return resolve(response);
          } catch (e) {
            uni.isLoggedIn = false;
            uni.$u.toast('登录已失效，请重新登录');
            return reject(e);
          }
        }

        return reject(res.data as ResponseData<T>);
      },
      // 响应失败
      fail(err) {
        uni.showModal({
          title: '提示',
          showCancel: false,
          content: '网络错误，换个网络试试',
          success: () => reject(err),
        });
      },
    });
  });
};

// uni.uploadFile封装
export const uniFileUpload = <T>(options: RequestOptions) => {
  // 1. 返回 Promise 对象
  return new Promise<ResponseData<T>>((resolve, reject) => {
    uni.uploadFile({
      ...options,
      // 响应成功
      success(res) {
        // 状态码 2xx，参考 axios 的设计
        if (res.statusCode >= 200 && res.statusCode < 300) {
          // 文件上传接口的rea.data的类型为string，这里转一下
          const resData = JSON.parse(res.data) as ResponseData<T>;
          resolve(resData);
        } else if (res.statusCode === 401) {
          // 401错误  -> 清理用户信息，跳转到登录页
          // userStore.clearUserInfo()
          // uni.navigateTo({ url: '/pages/login/login' })
          reject(res);
        } else {
          // 其他错误 -> 根据后端错误信息轻提示
          uni.showToast({
            icon: 'none',
            title: '文件上传错误',
          });
          reject(res);
        }
      },
      // 响应失败
      fail(err) {
        uni.showToast({
          icon: 'none',
          title: '网络错误，换个网络试试',
        });
        reject(err);
      },
    });
  });
};
